import { motion } from "framer-motion";
import { Bell, CalendarCheck, CheckCircle2, Mail } from "lucide-react";
import { DEMO } from "@/components/walkthrough/data";

const Field = ({ k, v }) => (
  <div><p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{k}</p><p className="text-slate-100 text-sm mt-0.5">{v}</p></div>
);

export function LeadStep() {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto rounded-2xl border border-orange-500/40 bg-slate-900 p-6" data-testid="wt-lead">
      <div className="flex items-center justify-between mb-5">
        <div><p className="text-white text-xl font-display font-bold">{DEMO.homeowner}</p><p className="text-xs font-mono text-slate-500">{DEMO.phone}</p></div>
        <span className="px-3 py-1 rounded-full text-xs border border-emerald-500/40 bg-emerald-500/15 text-emerald-300" data-testid="wt-lead-status">Estimate Booked</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field k="Property" v={DEMO.address} /><Field k="Problem" v={DEMO.issue} />
        <Field k="Roof" v={DEMO.roof} /><Field k="Urgency" v={DEMO.urgency} />
        <Field k="Email" v={DEMO.email} /><Field k="Est. job value" v={DEMO.value} />
        <Field k="Appointment (booked by text, simulated)" v={`${DEMO.slot} with ${DEMO.estimator}`} />
      </div>
      <p className="text-xs text-slate-500 mt-5">Collected automatically from the text conversation. The full chat is one click away.</p>
    </motion.div>
  );
}

export function AlertStep() {
  return (
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="wt-alert">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-800 bg-white text-slate-800 p-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><Mail className="w-4 h-4" /> New email · just now</div>
        <p className="font-semibold text-lg">New booked estimate: {DEMO.homeowner}</p>
        <p className="text-sm mt-2">{DEMO.issue}</p>
        <p className="text-sm text-slate-600 mt-1">{DEMO.address}</p>
        <p className="text-sm font-semibold mt-1" data-testid="wt-alert-appt">{DEMO.slot} · {DEMO.estimator}</p>
        <div className="mt-4 inline-block px-4 py-2 rounded-full bg-orange-500 text-slate-900 text-sm font-semibold">Open conversation</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
        className="rounded-2xl border border-slate-800 bg-slate-900 p-6 flex flex-col justify-center">
        <Bell className="w-8 h-8 text-orange-400 mb-3" />
        <p className="text-white font-semibold">Sent to the owner's inbox</p>
        <p className="text-slate-400 text-sm mt-1">One alert per qualified lead, so the team knows exactly who to call back.</p>
      </motion.div>
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const TIMES = ["8:00 AM", "10:00 AM", "1:00 PM", "3:00 PM"];
const BUSY = new Set(["Mon-10:00 AM", "Tue-1:00 PM", "Wed-8:00 AM", "Wed-3:00 PM", "Thu-1:00 PM", "Fri-10:00 AM"]);

export function CalendarStep() {
  const pick = "Thu-10:00 AM";
  return (
    <div className="max-w-3xl mx-auto rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="wt-calendar">
      <p className="text-slate-300 text-sm mb-4">Sarah's appointment lands in an open slot. Gray slots were already busy on the team's calendar, so they were never offered.</p>
      <div className="grid grid-cols-[80px_repeat(5,1fr)] gap-2 text-xs">
        <div />{DAYS.map((d) => <div key={d} className="text-center text-slate-400 font-mono">{d}</div>)}
        {TIMES.map((t) => [
          <div key={t} className="text-slate-500 font-mono self-center">{t}</div>,
          ...DAYS.map((d) => {
            const id = `${d}-${t}`, busy = BUSY.has(id), sel = pick === id;
            return (
              <div key={id} data-testid={`wt-slot-${d}-${t.replace(/[: ]/g, "")}`}
                className={`h-10 rounded-lg border flex items-center justify-center ${busy ? "bg-slate-800 border-slate-800 text-slate-600" : sel ? "bg-emerald-500 border-emerald-400 text-slate-900 font-semibold" : "border-slate-700 text-slate-400"}`}>
                {busy ? "Busy" : sel ? "Sarah M." : "Open"}
              </div>
            );
          }),
        ])}
      </div>
      <p className="mt-5 text-sm text-emerald-300 flex items-center gap-2" data-testid="wt-calendar-booked">
        <CalendarCheck className="w-4 h-4" /> {DEMO.slot}: free estimate, {DEMO.homeowner}, {DEMO.estimator}
      </p>
      <p className="text-xs text-amber-200/80 mt-2">Simulated: booking by text is shown for illustration and is not currently active.</p>
    </div>
  );
}

const STAGES = ["New", "Contacted", "Qualified", "Estimate Booked", "Won"];

export function DoneStep() {
  const [d, t] = ["Thursday", "10:00 AM"];
  return (
    <div className="max-w-3xl mx-auto text-center" data-testid="wt-done">
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {STAGES.map((s, i) => (
          <motion.span key={s} initial={{ opacity: 0.4 }} animate={{ opacity: i <= 3 ? 1 : 0.4 }} transition={{ delay: i * 0.25 }}
            className={`px-4 py-2 rounded-full text-sm border ${i === 3 ? "bg-emerald-500 text-slate-900 border-emerald-400 font-semibold" : i < 3 ? "border-slate-600 text-slate-300" : "border-slate-800 text-slate-600"}`}>{s}</motion.span>
        ))}
      </div>
      <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
      <p className="text-white text-2xl font-display font-bold" data-testid="wt-done-status">{DEMO.homeowner}: Estimate Booked</p>
      <p className="text-slate-400 mt-2">{d} {t} at {DEMO.address.replace(" (fictional)", "")} with {DEMO.estimator}</p>
      <p className="text-slate-500 text-sm mt-6">From missed call to booked estimate, without the roofer touching the phone.</p>
      <p className="text-xs text-amber-200/80 mt-2">Booking by text is simulated in this demo and is not currently active.</p>
    </div>
  );
}
